import { Injectable } from '@angular/core';
import { CameraPreview, CameraPreviewOptions, CameraPreviewPictureOptions } from '@awesome-cordova-plugins/camera-preview/ngx';
import { Camera, CameraOptions } from '@awesome-cordova-plugins/camera/ngx';
import { VideoCapturePlus, VideoCapturePlusOptions, MediaFile } from '@ionic-native/video-capture-plus/ngx';

@Injectable({
  providedIn: 'root'
})
export class CameraService {

  defaultCameraOptions: CameraOptions = {
    quality: 100,
    destinationType: this._camera.DestinationType.FILE_URI,
    encodingType: this._camera.EncodingType.JPEG,
    sourceType: this._camera.PictureSourceType.PHOTOLIBRARY,
    mediaType: this._camera.MediaType.PICTURE
  }

  defaultCameraPreviewOptions: CameraPreviewOptions = {
    x: 0,
    y: 0,
    width: 200,
    height: 200,
    toBack: false,
    previewDrag: false,
    tapPhoto: false,
    storeToFile: false,
    camera: 'rear'
  }

  defaultVideoOptions: VideoCapturePlusOptions = {
    limit: 1, // the nr of videos to record, default 1 (on iOS always 1)
    duration: 12, // max duration in seconds, default 0, which is 'forever'
    highquality: false, // set to true to override the default low quality setting
    frontcamera: true, // set to true to override the default backfacing camera setting. iOS: works fine, Android: YMMV (#18)
    // you'll want to sniff the useragent/device and pass the best overlay based on that.. assuming iphone here
    portraitOverlay: '', // put the png in your www folder
    landscapeOverlay: '', // not passing an overlay means no image is shown for the landscape orientation
    overlayText: ''
  }

  defaultCameraPreviewPictureOptions: CameraPreviewPictureOptions = {
    width: 1024,
    height: 1024,
    quality: 85
  }

  constructor(
    private _camera: Camera,
    private _cameraPreview: CameraPreview,
    private _videoCapturePlus: VideoCapturePlus
  ) { }

  getPicture(options: CameraOptions = undefined) {
    let cameraOptions = {
      ...this.defaultCameraOptions,
      ...options
    };
    return this._camera.getPicture(cameraOptions);
  }

  takePicture(options: CameraPreviewPictureOptions = undefined): Promise<any> {
    let previewPictureOptions = {
      ...this.defaultCameraPreviewPictureOptions,
      ...options
    };
    return this._cameraPreview.takePicture(options);
  }

  /**
   * 
   * @param options 
   * The camera will launch in respected x,y, width & height in front of the document
   */
  startCameraAbove(options: CameraPreviewOptions) {
    let cameraOptions = {
      ...this.defaultCameraPreviewOptions,
      ...options
    };
    this._cameraPreview.startCamera(cameraOptions);
  }

  /**
   * 
   * @param options 
   * The camera will launch background of document
   */
  startCameraBelow(options: CameraPreviewOptions = undefined) {
    document.querySelector('html').classList.add('camera-preview-mode');
    let cameraOptions:CameraPreviewOptions = {
      x: 0, y: 0,
      width: window.screen.width,
      height: window.screen.height,
      camera: "front",
      tapPhoto: false,
      previewDrag: false,
      toBack: true,
      storeToFile: false
    };
    this._cameraPreview.startCamera(cameraOptions);
  }

  /**
   * To stop the launched camera
   */
  stopCamera() {
    document.querySelector('html').classList.remove('camera-preview-mode');
    try{
      this._cameraPreview.stopCamera();
    } catch(err){
      console.log("No camera launched...");
    }
  }

  captureVideo(options:CBXVideoOption = {}):Promise<MediaFile[]> {
    let _videoOptions = {
      ...this.defaultVideoOptions,
      ...options
    };
    return this._videoCapturePlus.captureVideo(_videoOptions);
  }

  /**
   * 
   * @param base64PictureData 
   * @param rect_width 
   * @param rect_height 
   * @param callback 
   */
  cropImage(base64PictureData, rect_width, rect_height, callback) {

    // image will contain ORIGINAL image
    let image = new Image();

    // image will contain CROPPED image
    let canvas = document.createElement('canvas');
    let ctx = canvas.getContext('2d');

    // Load original image into image element
    image.src = 'data:image/jpeg;base64,' + base64PictureData;
    image.onload = function () {

      let scaleHeight = rect_height / image.height;
      let scaleWidth = rect_width / image.width;

      let scale;
      let x_img_units;
      let y_img_units;
      if (scaleHeight < scaleWidth) {
        scale = scaleWidth;
        x_img_units = 0;
        y_img_units = (image.height - (rect_height / scale)) / 2;
      } else {
        scale = scaleHeight;
        x_img_units = (image.width - (rect_width / scale)) / 2;
        y_img_units = 0;
      }

      let rect_width_img_units = rect_width / scale;
      let rect_height_img_units = rect_height / scale;

      // Set canvas size equivalent to interpolated rectangle size
      canvas.width = rect_width_img_units;
      canvas.height = rect_height_img_units;

      ctx.drawImage(image,
        x_img_units, y_img_units,                       // Start CROPPING from (x_img_units, y_img_units).
        rect_width_img_units, rect_height_img_units,    // Crop interpolated rectangle
        0, 0,                                           // Place the result at 0, 0 in the canvas,
        rect_width_img_units, rect_height_img_units);   // Crop interpolated rectangle

      // Get base64 representation of cropped image
      let cropped_img_base64 = canvas.toDataURL();

      // Now we are ready to send cropped image TO SERVER
      callback(cropped_img_base64);

      return cropped_img_base64;
    };
  }

}

export interface CBXCameraOption extends CameraPreviewOptions{}
export interface CBXPictureOption extends CameraPreviewPictureOptions{}
export interface CBXVideoOption extends VideoCapturePlusOptions {}
export interface CBXMediaFile extends MediaFile {}
